/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  exportConnexReleaseEffect,
  exportReleaseEffect,
  exportScReleaseEffect,
  exportTcReleaseEffect,
  fetchReleaseEffect,
  fetchVersionTypesDDLEffect,
} from 'app/store/Releases/effects';
import { routes } from 'app/routing/routesConfig';
import { useAppDispatch, useAppSelector } from 'app/store';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getRelease, getVersionTypes } from 'app/store/Releases/selectors';
import queryString from 'query-string';
import {
  connectToSoundcloudAttemptAction,
  connectToTuneCoreAttemptAction,
} from 'app/store/Mixpanel/slice';
import { getWebRedirectUrl, openURL } from 'app/utils/utils';
import { addAlertAction } from 'app/store/Alerts/slice';
import {
  getConnections,
  getCreateConnectionLink,
} from 'app/store/Profile/selectors';
import { useFetch } from 'app/utils/hooks';
import {
  AccountConnectionProviderId,
  CreateConnectionResponse,
} from 'types/profile';
import { useDisclosure } from '@chakra-ui/react';
import { ExportReleaseData, SoundCloudRelease } from 'types/releases';
import {
  MultiTrackExportRequestData,
  SingleTrackExportRequestData,
} from 'app/components/Releases/ExportSoundCloudReleaseDialog/types';
import { fetchConnectionsEffect } from 'app/store/Profile/effects';
import { getOptions } from 'app/components/Song/Details/SongDetailsForm/utils';
import { DDL } from 'types/general.types';
import { EnumValidation } from 'types/generic.types';
import { ExportTuneCoreReleaseFormValues } from 'app/components/Releases/ExportTuneCoreReleaseDialog/types';
import { ExportConnexReleaseFormValues } from 'app/components/Releases/ExportSendLabelV2Release';
import { config } from 'config';

export const useFetchRelease = () => {
  const dispatch = useAppDispatch();
  const versionTypesDDL = useAppSelector(getVersionTypes);

  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const parsedParams = queryString.parse(location.search);

  const fetchRelease = useCallback(async () => {
    try {
      await dispatch(
        fetchReleaseEffect({
          guid: params.guid as string,
        })
      ).unwrap();

      if (!versionTypesDDL) {
        await dispatch(fetchVersionTypesDDLEffect());
      }

      setLoading(false);
    } catch (e) {
      setLoading(false);
      navigate(routes.releases.path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.guid]);

  useEffect(() => {
    if (!parsedParams?.shouldNotFetch) {
      fetchRelease();
    } else {
      setLoading(false);
      navigate({ search: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRelease]);

  return { loading };
};

export const useFetchAccountConnection = (
  providerIdProp?: AccountConnectionProviderId
) => {
  const dispatch = useAppDispatch();
  const connections = useAppSelector(getConnections);
  const fetchAccountConnection = useCallback(
    async (providerId?: AccountConnectionProviderId) => {
      try {
        return await dispatch(
          fetchConnectionsEffect({ providerId: providerId || providerIdProp })
        ).unwrap();
      } catch (error) {
        return [];
      }
    },
    [dispatch, providerIdProp]
  );

  return {
    fetchAccountConnection,
    connection: connections.find((el) => el.providerId === providerIdProp),
  };
};

export const useCheckConnection = () => {
  const { fetchAccountConnection } = useFetchAccountConnection();

  const checkConnection = useCallback(
    async (providerId: AccountConnectionProviderId) => {
      const connections = await fetchAccountConnection(providerId);
      return connections.find((el) => el.providerId === providerId)?.guid;
    },
    [fetchAccountConnection]
  );

  return { checkConnection };
};

export const useDraftReleaseConnection = (exportGuid?: string) => {
  const dispatch = useAppDispatch();
  const createConnectionLink = useAppSelector(getCreateConnectionLink);
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { guid } = useParams();
  const { callAPI } = useFetch<{}, CreateConnectionResponse>();

  const handleOnConnect = async (providerId: AccountConnectionProviderId) => {
    if (createConnectionLink) {
      try {
        const releasePathname = routes.release.path.replace(
          ':guid',
          guid || ''
        );
        const { response } = await callAPI(createConnectionLink.href, {
          providerId,
          releaseExportGuid: exportGuid,
          redirectUrl: config.isElectronBuild
            ? location.pathname
            : getWebRedirectUrl(releasePathname, { newTab: true }),
        });

        if (providerId === 'SoundCloud') {
          dispatch(connectToSoundcloudAttemptAction());
        }
        if (providerId === 'TuneCore') {
          dispatch(connectToTuneCoreAttemptAction({ page: 'Release' }));
        }

        openURL(response.accountConnection._links.redirect.href, {
          isExternal: true,
        });
      } catch (e: any) {
        dispatch(
          addAlertAction({
            message: `Failed to establish connection with ${providerId}. Please try again`,
          })
        );
      } finally {
        onClose();
      }
    }
  };

  return {
    handleOnConnect,
    isOpen,
    onOpen,
    onClose,
  };
};

export const useReleaseExport = () => {
  const dispatch = useAppDispatch();
  const release = useAppSelector(getRelease);
  const params = useParams();

  const exportRelease = async (data: {
    data: ExportReleaseData;
    download: boolean;
  }) =>
    dispatch(
      exportReleaseEffect({ ...data, releaseGuid: params.guid })
    ).unwrap();

  const exportSoundCloudRelease = async (
    data: SingleTrackExportRequestData | MultiTrackExportRequestData
  ) =>
    dispatch(
      exportScReleaseEffect({
        data,
        releaseGuid: release?.guid as string,
        audioCount: (release as SoundCloudRelease).versions.length,
      })
    ).unwrap();

  const exportTuneCoreRelease = async (
    data: ExportTuneCoreReleaseFormValues,
    accountConnectionGuid?: string
  ) =>
    dispatch(
      exportTcReleaseEffect({
        data,
        releaseGuid: release?.guid as string,
        accountConnectionGuid,
      })
    ).unwrap();

  const exportConnexRelease = useCallback(
    async (
      data: ExportConnexReleaseFormValues,
      accountConnectionGuid?: string
    ) => {
      return dispatch(
        exportConnexReleaseEffect({
          data,
          releaseGuid: release?.guid as string,
          accountConnectionGuid,
        })
      ).unwrap();
    },
    [release?.guid, dispatch]
  );

  return {
    exportRelease,
    exportSoundCloudRelease,
    exportTuneCoreRelease,
    exportConnexRelease,
  };
};

export const useGenres = ({
  genresEnum,
  genresDDL,
  validationSchemaFetched,
}: {
  genresDDL: DDL;
  validationSchemaFetched: boolean;
  genresEnum?: EnumValidation;
}) => {
  const genres = useMemo(() => {
    if (validationSchemaFetched && genresEnum) {
      return getOptions(genresEnum, genresDDL, true);
    }
    return [];
  }, [genresDDL, genresEnum, validationSchemaFetched]);

  return { genres };
};
